import React, { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaPlus } from 'react-icons/fa';
import { BackButton } from '../common/styles';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  ControlsContainer,
  MainContainer,
  HeaderButton,
} from './styles';
import MeetingWizard from './components/MeetingWizard';
import MeetingForm from './components/MeetingForm';
import MeetingFilters from './components/MeetingFilters';
import MeetingGrid from './components/MeetingGrid';
import ResultsCount from '../common/ResultsCount';
import useMeetingsStoreRepository from '../../store/meetingsStoreRepository';
import useMeetingsRepositorySearch from '../../hooks/useMeetingsRepositorySearch';
import useGridNavigation from '../../hooks/useGridNavigation';
import { useAuth } from '../../contexts/AuthContext';
import { MEETING_ATTENDEE_TYPES } from '../../constants/meetingAttendeeTypes';

const Meetings = ({
  title = 'Check-ins (1:1 Meetings)',
  routePrefix = '/meetings',
  useStore = useMeetingsStoreRepository,
  showBackButton = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  const gridRef = useRef(null);

  // Store state (meetings data, loading, pagination, and multi-select filters)
  const {
    meetings,
    isLoading,
    isLoadingMore,
    pageInfo,
    fetchMeetingsWithReset,
    fetchMeetingsLoadMore,
    setIsReturningFromDetails,
    isReturningFromDetails,
    // Multi-select filters (directly from store like IDP pattern)
    selectedParticipants,
    setSelectedParticipants,
    selectedTypes,
    setSelectedTypes,
    selectedStatuses,
    setSelectedStatuses,
    resetFilters,
  } = useStore();

  // Other filter state (search with debounce, simple selects)
  const {
    searchInputValue,
    setSearchInputValue,
    datePreset,
    setDatePreset,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    actionItemsFilter,
    setActionItemsFilter,
    talkingPointsFilter,
    setTalkingPointsFilter,
  } = useMeetingsRepositorySearch();

  const isNewMode = location.pathname === `${routePrefix}/new`;
  const isViewMode = location.pathname.endsWith('/view');
  const isFormMode = id && !isNewMode;

  // Grid navigation hook for virtualization and scroll handling
  const {
    handleItemClick,
    handleGridScroll,
    showLoadingSpinner,
    shouldHideContent,
    loadMoreData,
    navigateWithScrollState,
    handleBackToItems,
    shouldLoadData,
  } = useGridNavigation({
    gridRef,
    items: meetings,
    columnCount: 3,
    rowHeight: 280,
    routes: {
      list: routePrefix,
      detail: (meetingId) => `${routePrefix}/${meetingId}`,
      new: `${routePrefix}/new`,
      additionalRoutes: [
        { path: '/view', shouldLoadData: false }
      ]
    },
    onReturnFromDetail: () => {
      setIsReturningFromDetails(true);
    },
    onNavigateAway: resetFilters,
    loadMoreDataCallback: fetchMeetingsLoadMore,
    isLoading,
    isLoadingMore,
    pageInfo,
  });

  // Fetch meetings on mount
  useEffect(() => {
    if (shouldLoadData() && !isReturningFromDetails) {
      fetchMeetingsWithReset();
    }
    if (isReturningFromDetails) {
      setIsReturningFromDetails(false);
    }
  }, [shouldLoadData, isReturningFromDetails]);

  // Navigate to view meeting (preserves scroll position)
  const handleViewMeeting = useCallback((meeting) => {
    navigateWithScrollState(meeting, `${routePrefix}/${meeting.id}/view`);
  }, [navigateWithScrollState, routePrefix]);

  // Navigate to edit meeting (preserves scroll position)
  const handleEditMeeting = useCallback((meeting) => {
    handleItemClick(meeting);
  }, [handleItemClick]);

  const handleNewMeeting = useCallback(() => {
    navigate(`${routePrefix}/new`);
  }, [navigate, routePrefix]);

  const handleWizardComplete = useCallback((meetingId) => {
    navigate(`${routePrefix}/${meetingId}`);
  }, [navigate, routePrefix]);

  const handleWizardBack = useCallback(() => {
    navigate(routePrefix);
  }, [navigate, routePrefix]);

  const handleFormBack = useCallback((hasChanges) => {
    if (hasChanges) {
      fetchMeetingsWithReset();
    }
    handleBackToItems();
  }, [handleBackToItems, fetchMeetingsWithReset]);

  // Check if current user is owner of a meeting
  const isUserOwner = useCallback((meeting) => {
    const ownerAttendee = meeting.attendees?.find(a => a.attendeeType?.code === MEETING_ATTENDEE_TYPES.OWNER);
    return ownerAttendee?.employeeId === user?.employee?.id;
  }, [user]);

  // Render wizard for new meeting
  if (isNewMode) {
    return (
      <MeetingWizard
        onBack={handleWizardBack}
        onComplete={handleWizardComplete}
      />
    );
  }

  // Render form for viewing/editing
  if (isFormMode) {
    return (
      <MeetingForm
        meetingId={id}
        isViewMode={isViewMode}
        onBack={handleFormBack}
      />
    );
  }

  // Render meetings list
  return (
    <Container>
      <Header>
        <HeaderLeft>
          {showBackButton && (
            <BackButton onClick={() => navigate('/')}>
              <FaArrowLeft /> Back to Home
            </BackButton>
          )}
          <Title>{title}</Title>
        </HeaderLeft>
        <HeaderButton onClick={handleNewMeeting}>
          <FaPlus /> New Meeting
        </HeaderButton>
      </Header>

      <Content>
        <ControlsContainer show={true}>
          <MeetingFilters
            searchInputValue={searchInputValue}
            setSearchInputValue={setSearchInputValue}
            datePreset={datePreset}
            setDatePreset={setDatePreset}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            selectedParticipants={selectedParticipants}
            setSelectedParticipants={setSelectedParticipants}
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            actionItemsFilter={actionItemsFilter}
            setActionItemsFilter={setActionItemsFilter}
            talkingPointsFilter={talkingPointsFilter}
            setTalkingPointsFilter={setTalkingPointsFilter}
          />
        </ControlsContainer>

        <MainContainer>
          <ResultsCount count={pageInfo.totalCount} singular="meeting" plural="meetings" />

          <MeetingGrid
            meetings={meetings}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            pageInfo={pageInfo}
            handleItemClick={handleItemClick}
            handleGridScroll={handleGridScroll}
            showLoadingSpinner={showLoadingSpinner}
            shouldHideContent={shouldHideContent}
            loadMoreData={loadMoreData}
            gridRef={gridRef}
            onView={handleViewMeeting}
            onEdit={handleEditMeeting}
            isUserOwner={isUserOwner}
            searchInputValue={searchInputValue}
          />
        </MainContainer>
      </Content>
    </Container>
  );
};

export default Meetings;
